import React, { useEffect, useRef } from 'react';
import { Client } from '@stomp/stompjs';
import type { WsResourceEvent } from './useStoreUpdater';
import { storeRegistry } from './stores';
import { setWsStatus } from './wsStatusStore';

const WS_URL = process.env.REACT_APP_SOCKET_BASE_URL ?? '';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface WsProviderProps {
  children?: React.ReactNode;
}

/**
 * Establishes a single STOMP WebSocket connection for the entire app.
 * Reads the token from localStorage each time it (re)connects.
 * Skips the connection entirely if no token is present.
 *
 * WS events are dispatched directly to the appropriate store via storeRegistry,
 * bypassing React state entirely to prevent dropped events on rapid delivery.
 *
 * Mount this *inside* AuthProvider so the token is already in localStorage.
 */
export const WsProvider: React.FC<WsProviderProps> = ({ children }) => {
  const clientRef = useRef<Client | null>(null);

  useEffect(() => {
    // Read token at effect time (after auth has had a chance to write it)
    const token = localStorage.getItem('token');

    if (!token) {
      console.warn('[WS] Skipping connection — no token in localStorage');
      return;
    }

    if (!WS_URL) {
      console.warn('[WS] Skipping connection — REACT_APP_WS_URL is not set');
      return;
    }

    console.log('[WS] Connecting to', WS_URL);

    const client = new Client({
      brokerURL: WS_URL,
      // Provide fresh token at connection time (also used on reconnect)
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      // Re-read token on every reconnect attempt in case it was refreshed
      beforeConnect: async () => {
        setWsStatus({ status: 'connecting', error: null });
        const freshToken = localStorage.getItem('token');
        if (freshToken) {
          client.connectHeaders = { Authorization: `Bearer ${freshToken}` };
        }
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 5000,
      heartbeatOutgoing: 5000,
    });

    client.onConnect = () => {
      console.log('[WS] Connected');
      setWsStatus({ status: 'connected', error: null });
      client.subscribe('/topic/resource', (message) => {
        try {
          const event = JSON.parse(message.body) as WsResourceEvent;
          const store = storeRegistry[event.resource?.toUpperCase()];
          if (!store) {
            console.warn('[WS] No store for resource:', event.resource);
            return;
          }
          switch (event.type) {
            case 'CREATE': store.applyCreate(event.payload as any); break;
            case 'UPDATE': store.applyUpdate(event.payload as any); break;
            case 'DELETE': store.applyDelete(event.payload as string); break;
            default: console.warn('[WS] Unknown event type:', event.type);
          }
        } catch (e) {
          console.warn('[WS] Failed to parse message', e);
        }
      });
    };

    client.onStompError = (frame) => {
      console.error('[WS] STOMP error', frame);
      setWsStatus({ status: 'disconnected', error: frame.body || 'STOMP error' });
    };

    client.onDisconnect = () => {
      console.log('[WS] Disconnected');
      setWsStatus({ status: 'disconnected', error: null });
    };

    client.onWebSocketClose = () => {
      console.log('[WS] WebSocket closed');
      setWsStatus({ status: 'disconnected', error: null });
    };

    client.onWebSocketError = (event) => {
      console.error('[WS] WebSocket error', event);
      setWsStatus({ status: 'disconnected', error: 'WebSocket error' });
    };

    setWsStatus({ status: 'connecting', error: null });
    client.activate();
    clientRef.current = client;

    return () => {
      client.deactivate();
      clientRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ intentionally runs once on mount; token is read fresh inside beforeConnect on reconnects

  return <>{children}</>;
};
